import { Directive, effect, ElementRef, inject, input } from '@angular/core';

@Directive({
  selector: '[appScrollToId]',
  standalone: true,
  exportAs: 'appScrollToId',
})
export class ScrollToIdDirective {
  private readonly elementRef = inject(ElementRef);

  public readonly appScrollToId = input<string | null | undefined>(undefined);
  public readonly offset = input<number>(0);
  public readonly center = input<boolean>(true);

  constructor() {
    effect(() => {
      const id = this.appScrollToId();
      if (id) {
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          this.scrollToElement(id);
        }, 0);
      }
    });
  }

  /**
   * Public method to manually trigger scroll to an element
   */
  public scrollTo(id: string | null | undefined): void {
    if (id) {
      setTimeout(() => {
        this.scrollToElement(id);
      }, 200);
    }
  }

  private scrollToElement(id: string): void {
    const element = document.getElementById(id);
    if (element) {
      this.scrollIntoParentView(element, this.elementRef.nativeElement);
    }
  }

  private scrollIntoParentView(child: HTMLElement, scrollableParent: HTMLElement): void {
    const childRect = child.getBoundingClientRect();
    const parentRect = scrollableParent.getBoundingClientRect();

    const offsetTop = childRect.top - parentRect.top + scrollableParent.scrollTop;
    const offsetLeft = childRect.left - parentRect.left + scrollableParent.scrollLeft;

    // Apply custom offset
    const finalOffsetTop = offsetTop - this.offset();

    // Center the element horizontally within the scrollable parent (if enabled)
    const finalOffsetLeft = this.center()
      ? offsetLeft - (scrollableParent.offsetWidth - child.offsetWidth) / 2
      : offsetLeft;

    scrollableParent.scrollTo({
      top: Math.max(0, finalOffsetTop),
      left: finalOffsetLeft,
      behavior: 'smooth',
    });
  }
}
